import { useId, useState } from 'react';
import type {
  OutreachOutlierGroup,
  OutreachOutlierGroupCode,
  OutreachOutlierSummary,
} from '@aegislab/shared';
import { useOutlierRows } from '@/entities/outreach-candidate';
import { Button, Checkbox, LockIcon, Pill, type PillTone } from '@/shared/ui/atoms';
import { actionLabel, foldSharedRows, groupCopy, type OutlierLine } from '../model/outlierGroups';
import * as s from './OutlierGroupList.css';

export interface OutlierGroupListProps {
  scenarioId: string;
  draftId: string;
  summary: OutreachOutlierSummary;
  /** 초안에서 뺀 사람. 묶음을 받은 뒤 화면에서 뺀 것도 든다. */
  excludedIds: ReadonlySet<string>;
  onExclude: (ids: string[]) => void;
  onInclude: (ids: string[]) => void;
  /** 묶음 전체 빼기. 전체 id 를 페이지로 받아 와야 해서 비동기다. */
  onExcludeGroup: (code: OutreachOutlierGroupCode) => void;
  /** 이 화면에서 묶음째 뺀 사람 — 코드별. 다시 넣기 버튼이 이것을 쓴다. */
  excludedByGroup: ReadonlyMap<OutreachOutlierGroupCode, readonly string[]>;
  /** 묶음 id 를 받아 오는 중인 묶음. */
  pendingCode: OutreachOutlierGroupCode | null;
  /** 문제 없는 인원 줄의 「목록」 — 후보 표를 연다. */
  listOpen: boolean;
  onToggleList: () => void;
}

const n = new Intl.NumberFormat('ko-KR');

/** 기본으로 나가는 묶음만 이 화면에서 뺄 수 있다. 나머지는 서버 판정이거나 발송 확인 몫이다. */
function canExclude(group: OutreachOutlierGroup): boolean {
  return group.defaultAction === 'INCLUDE';
}

function tone(group: OutreachOutlierGroup): PillTone {
  if (!group.reversible) return 'urgent';
  return group.defaultAction === 'INCLUDE' ? 'warning' : 'neutral';
}

/**
 * 「먼저 볼 것」 — 승인 시안 CandidatesAfter(2026-09-24).
 *
 * 2만 명을 사람이 다 훑지 못하므로 서버가 가른 다섯 묶음만 보인다. 묶음 줄은 진짜 `button` 이고
 * `aria-expanded` 로 펼침을 알린다 — 키보드로 Enter·Space 를 누르면 그대로 열린다.
 *
 * 묶음 단위로 뺄 수 있는 것은 **기본으로 나가는 묶음**(다른 팀이 최근 연락함)뿐이다. 차단·보류와
 * 주소 없음은 되돌릴 수 없어 잠금을 보이고, 이미 답한 사람을 다시 넣는 것은 발송 확인 단계가
 * 그 발송에만 건다(`SendConfirmReinclude`, 12bcfe19) — 초안에 저장하면 다음 묶음까지 따라간다.
 */
export function OutlierGroupList({
  scenarioId,
  draftId,
  summary,
  excludedIds,
  onExclude,
  onInclude,
  onExcludeGroup,
  excludedByGroup,
  pendingCode,
  listOpen,
  onToggleList,
}: OutlierGroupListProps) {
  const [open, setOpen] = useState<OutreachOutlierGroupCode | null>(null);
  const baseId = useId();

  return (
    <div className={s.list}>
      {summary.groups.map((group) => {
        const copy = groupCopy(group);
        const isOpen = open === group.code;
        const regionId = `${baseId}-${group.code}`;
        const removed = excludedByGroup.get(group.code) ?? [];
        const removedNow = removed.length > 0 && removed.every((id) => excludedIds.has(id));
        return (
          <div key={group.code} className={s.group}>
            <button
              type="button"
              className={s.head}
              aria-expanded={isOpen}
              aria-controls={regionId}
              onClick={() => setOpen(isOpen ? null : group.code)}
            >
              <span className={s.dot[group.code]} aria-hidden="true" />
              <span className={s.titleBox}>
                <span className={s.title}>{copy.title}</span>
                {copy.counts === null ? null : <span className={s.counts}>{copy.counts}</span>}
              </span>
              <span className={s.count}>{n.format(group.count)}</span>
              <Pill tone={tone(group)} className={s.chip}>
                {group.reversible ? null : <LockIcon size={12} />}
                {actionLabel(group)}
              </Pill>
            </button>
            {isOpen ? (
              <div id={regionId} className={s.body}>
                {copy.note === null ? null : <p className={s.note}>{copy.note}</p>}
                {group.count === 0 ? (
                  <p className={s.note}>이 묶음에 든 사람이 없습니다.</p>
                ) : (
                  <OutlierAllRows
                    scenarioId={scenarioId}
                    draftId={draftId}
                    group={group}
                    excludedIds={excludedIds}
                    onExclude={onExclude}
                    onInclude={onInclude}
                  />
                )}
                <GroupNote group={group} />
                <div className={s.actions}>
                  {canExclude(group) && removedNow ? (
                    <Button variant="secondary" size="xs" onClick={() => onInclude([...removed])}>
                      빼 둔 {n.format(removed.length)}명 다시 넣기
                    </Button>
                  ) : null}
                  {canExclude(group) && !removedNow && group.count > 0 ? (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => onExcludeGroup(group.code)}
                      disabled={pendingCode !== null}
                    >
                      {pendingCode === group.code
                        ? '명단을 받는 중'
                        : `이 ${n.format(group.count)}명 모두 빼기`}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      <div className={s.cleanRow}>
        <span className={s.dotClean} aria-hidden="true" />
        <span className={s.title}>문제 없음</span>
        <span className={s.count}>{n.format(summary.cleanCount)}</span>
        <Button variant="ghost" size="xs" onClick={onToggleList} aria-expanded={listOpen}>
          {listOpen ? '후보 목록 닫기' : '후보 목록 보기'}
        </Button>
      </div>
    </div>
  );
}

function GroupNote({ group }: { group: OutreachOutlierGroup }) {
  switch (group.code) {
    case 'DO_NOT_SEND':
      return <p className={s.note}>차단과 보류는 관계 창에서만 풀 수 있습니다.</p>;
    case 'ALREADY_ANSWERED':
      return (
        <p className={s.note}>
          이미 답한 사람을 다시 넣을지는 발송 확인 단계에서 정합니다 — 그 발송에만 들어갑니다.
        </p>
      );
    case 'SHARED_ADDRESS':
      return <p className={s.note}>쌍을 따로 보낼지는 발송 확인 단계에서 정합니다.</p>;
    case 'OTHER_TEAM_RECENT':
    case 'NO_ADDRESS':
      return null;
  }
}

function OutlierLines({
  group,
  lines,
  excludedIds,
  onExclude,
  onInclude,
}: {
  group: OutreachOutlierGroup;
  lines: OutlierLine[];
  excludedIds: ReadonlySet<string>;
  onExclude: (ids: string[]) => void;
  onInclude: (ids: string[]) => void;
}) {
  return (
    <ul className={s.rows}>
      {lines.map((line) => {
        const out = line.ids.every((id) => excludedIds.has(id));
        return (
          <li key={line.key} className={s.row}>
            <span className={s.handle}>{line.label}</span>
            <span className={s.detail}>{line.detail}</span>
            {canExclude(group) ? (
              <Checkbox
                checkboxSize="sm"
                checked={!out}
                label="포함"
                onChange={() => (out ? onInclude(line.ids) : onExclude(line.ids))}
                aria-label={`${line.label} 포함`}
              />
            ) : (
              <span className={s.detail}>{actionLabel(group).split(' · ')[0]}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 묶음 명단 — 묶음을 누르면 **바로** 전체 명단을 200명씩 넘겨 보인다(이강주 팀장 요청, 퀘스트
 * 2f546c56 질문 답글 2026-10-01). 예전에는 미리보기 몇 명을 먼저 보이고 「N명 전부 보기」 를 한 번
 * 더 눌러야 했다.
 *
 * 받는 사이에는 요약에 실려 온 미리보기 행을 먼저 보여 빈 칸이 깜빡이지 않게 한다. 명단은 자기
 * 칸 안에서만 스크롤한다 — 200행이 그대로 펼쳐지면 아래 회신 큐가 수천 px 밀려난다.
 */
function OutlierAllRows({
  scenarioId,
  draftId,
  group,
  ...rest
}: {
  scenarioId: string;
  draftId: string;
  group: OutreachOutlierGroup;
  excludedIds: ReadonlySet<string>;
  onExclude: (ids: string[]) => void;
  onInclude: (ids: string[]) => void;
}) {
  const [offsets, setOffsets] = useState<number[]>([0]);
  const offset = offsets[offsets.length - 1] ?? 0;
  const page = useOutlierRows(scenarioId, draftId, group.code, offset);

  if (page.isPending || page.isError) {
    return (
      <>
        <p className={s.note}>
          {page.isPending ? '전체 명단을 불러오는 중입니다.' : '전체 명단을 불러오지 못했습니다.'}
        </p>
        {group.preview.length > 0 ? (
          <OutlierLines group={group} lines={foldSharedRows(group.preview)} {...rest} />
        ) : null}
      </>
    );
  }

  const next = page.data.nextOffset;
  const paged = next !== null || offsets.length > 1;
  return (
    <div className={s.all}>
      <p className={s.note}>
        {n.format(page.data.total)}명 중 {n.format(offset + 1)}~
        {n.format(offset + page.data.rows.length)}번째를 보고 있습니다.
      </p>
      {/* 되돌릴 수 없는 묶음은 칸 안에 누를 것이 없어 칸 자체가 포커스를 받아야 키보드로 내린다. */}
      <div
        className={s.scroll}
        role="region"
        aria-label={`${groupCopy(group).title} 명단`}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- axe 의 scrollable-region-focusable 가 요구하는 포커스다 (두 린터의 판단이 엇갈리는 지점)
        tabIndex={0}
      >
        <OutlierLines group={group} lines={foldSharedRows(page.data.rows)} {...rest} />
      </div>
      {paged ? (
        <div className={s.actions}>
          <Button
            variant="ghost"
            size="xs"
            disabled={offsets.length === 1}
            onClick={() => setOffsets((list) => list.slice(0, -1))}
          >
            이전
          </Button>
          <Button
            variant="ghost"
            size="xs"
            disabled={next === null}
            onClick={() => next !== null && setOffsets((list) => [...list, next])}
          >
            다음
          </Button>
        </div>
      ) : null}
    </div>
  );
}
